import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Calendar, X } from "lucide-react";
import { utcToLocalInput, localInputToUtc, todayLocalInputDefault } from "../../lib/dates";

/**
 * Compact inline date/time picker. Shown as an icon-only "Calendar" chip
 * when the row has no due date, or reused inside the due badge to allow
 * quick reschedule from the list without opening the panel.
 *
 * When there is no existing due_at, the input is prefilled with today at
 * 17:00 in the tenant tz so a single click on Save produces a valid
 * "today" due date — matches how Linear/Todoist behave.
 */
export default function InlineDatePicker({
  dueAt,
  tenantTz,
  onChange,
  testId,
  triggerClassName = "",
  children,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(
    dueAt ? utcToLocalInput(dueAt, tenantTz || "UTC") : todayLocalInputDefault(tenantTz),
  );

  const save = () => {
    if (!val) {
      onChange(null);
    } else {
      onChange(localInputToUtc(val, tenantTz || "UTC"));
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          onClick={(e) => {
            e.stopPropagation();
            // Every open re-syncs to the current value; if none, prefill today
            setVal(
              dueAt ? utcToLocalInput(dueAt, tenantTz || "UTC") : todayLocalInputDefault(tenantTz),
            );
          }}
          className={
            triggerClassName ||
            "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 transition-colors"
          }
        >
          {children || (
            <>
              <Calendar className="h-3 w-3" />
              {t("tasks.set_date")}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-64 p-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
          {t("tasks.due_at")} ({tenantTz || "UTC"})
        </div>
        <Input
          type="datetime-local"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          className="text-sm"
          data-testid="inline-date-input"
        />
        <div className="mt-3 flex items-center justify-between gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setVal("");
              onChange(null);
              setOpen(false);
            }}
            className="text-red-600 hover:bg-red-50 hover:text-red-700 gap-1"
            data-testid="inline-date-clear"
          >
            <X className="h-3 w-3" />
            {t("tasks.clear_date")}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={save}
            className="bg-zinc-900 text-white hover:bg-zinc-800"
            data-testid="inline-date-save"
          >
            {t("tasks.save")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
